<template>
  <ts-loading-banner :loading="waiting">
    <div class="ts-space-y-4">
      <div class="row">
        <div class="col-4 ts-space-y-1">
          <label class="required">{{ $t('studentDailyActivity.class') }}</label>
          <a-select style="width: 100%" v-model.number="model.class_id" :options="classlists"
            :disabled="showOnly || isUpdate"></a-select>
          <div class="validate" v-if="errors.has('model.class_id')">
            {{ errors.first('model.class_id') }}
          </div>
        </div>

        <div class="col-4 ts-space-y-1">
          <label class="required">{{ $t('studentDailyActivity.student') }}</label>
          <a-select style="width: 100%" v-model="model.student_id" :options="studentlists" :show-search="true"
            :maxTagCount="2" :loading="fetching_student" :disabled="showOnly || isUpdate" :filter-option="(input, option) =>
              option.componentOptions.children[0].text
                .toLowerCase()
                .indexOf(input.toLowerCase()) >= 0
              " @change="checkData()"></a-select>
          <div class="validate" v-if="errors.has('student_id')">
            {{ errors.first('student_id') }}
          </div>
        </div>
        <div class="col-4 ts-space-y-1">
          <label class="required">{{ $t('studentDailyActivity.date') }}</label>
          <a-date-picker v-model="model.activity_date" style="width: 100%" placeholder="DD-MM-YYYY"
            valueFormat="DD-MM-YYYY" format="DD-MM-YYYY" @change="onChangeActivityDate" :disabled="showOnly" />
          <div class="invalid-feedback" v-if="errors.has('activity_date')">
            {{ errors.first('activity_date') }}
          </div>
        </div>
      </div>

      <div class="ts-space-y-3">
        <!-- Today's Special Activities -->
        <div class="row">
          <label class="ts-whitespace-no-wrap col-form-label col-4 ts-align-top ts-pt-0">
            {{ $t('studentDailyActivity.specialActivitiesToday') }}
          </label>
          <div class="col-8">
            <textarea v-model="model.meta.special_activity" :disabled="showOnly" rows="2"
              class="form-control"></textarea>
          </div>
        </div>

        <!-- For morning snack I had -->
        <div class="row">
          <label class="ts-whitespace-no-wrap col-form-label col-4">
            {{ $t('studentDailyActivity.forMorningSnackIHad') }}
          </label>
          <div class="col-8">
            <div class="d-flex flex-nowrap align-items-center">
              <div class="col-form-label ts-whitespace-no-wrap mr-3">
                <a-checkbox v-model="model.meta.morning_snack.fruits" :disabled="showOnly">
                  {{ $t('studentDailyActivity.fruits') }}
                </a-checkbox>
              </div>
              <div class="col-form-label ts-whitespace-no-wrap mr-3">
                <a-checkbox v-model="model.meta.morning_snack.bread" :disabled="showOnly">
                  {{ $t('studentDailyActivity.bread') }}
                </a-checkbox>
              </div>
              <div class="col-form-label ts-whitespace-no-wrap mr-3">
                <a-checkbox v-model="model.meta.morning_snack.others" :disabled="showOnly">
                  {{ $t('studentDailyActivity.others') }}
                </a-checkbox>
              </div>
              <input v-model="model.meta.morning_snack.specify"
                :disabled="showOnly || !model.meta.morning_snack.others" type="text" class="form-input"
                style="flex: 1" :placeholder="$t('studentDailyActivity.pleaseSpecify')" />
            </div>
          </div>
        </div>

        <!-- For lunch I had -->
        <div class="row">
          <label class="ts-whitespace-no-wrap col-form-label col-4">
            {{ $t('studentDailyActivity.forLunchIHad') }}
          </label>
          <div class="col-8">
            <div class="d-flex flex-nowrap align-items-center">
              <div class="col-form-label ts-whitespace-no-wrap mr-3">
                <a-checkbox :checked="model.meta.lunch.dishes === 1" @change="onSelectLunchDish(1)"
                  :disabled="showOnly">
                  1 {{ $t('studentDailyActivity.dish') }}
                </a-checkbox>
              </div>
              <div class="col-form-label ts-whitespace-no-wrap mr-3">
                <a-checkbox :checked="model.meta.lunch.dishes === 2" @change="onSelectLunchDish(2)"
                  :disabled="showOnly">
                  2 {{ $t('studentDailyActivity.dishes') }}
                </a-checkbox>
              </div>
              <div class="col-form-label ts-whitespace-no-wrap mr-3">
                <a-checkbox :checked="model.meta.lunch.dishes === 3" @change="onSelectLunchDish(3)"
                  :disabled="showOnly">
                  3 {{ $t('studentDailyActivity.dishes') }}
                </a-checkbox>
              </div>
              <div class="col-form-label ts-whitespace-no-wrap mr-3">
                <a-checkbox v-model="model.meta.lunch.others" :disabled="showOnly">
                  {{ $t('studentDailyActivity.others') }}
                </a-checkbox>
              </div>
              <input v-model="model.meta.lunch.specify" :disabled="showOnly || !model.meta.lunch.others"
                type="text" class="form-input" style="flex: 1"
                :placeholder="$t('studentDailyActivity.pleaseSpecify')" />
            </div>
          </div>
        </div>

        <!-- I slept -->
        <div class="row">
          <label class="ts-whitespace-no-wrap col-form-label col-4">
            {{ $t('studentDailyActivity.iSlept') }}
          </label>
          <div class="col-8">
            <div class="d-flex flex-nowrap align-items-center">
              <div class="col-form-label ts-whitespace-no-wrap mr-3">
                <a-checkbox v-model="model.meta.sleep.yes" @change="onSleepYes" :disabled="showOnly">
                  {{ $t('studentDailyActivity.yes') }}
                </a-checkbox>
              </div>
              <div class="col-form-label ts-whitespace-no-wrap mr-3">
                <a-checkbox v-model="model.meta.sleep.no" @change="onSleepNo" :disabled="showOnly">
                  {{ $t('studentDailyActivity.no') }}
                </a-checkbox>
              </div>
              <input v-model="model.meta.sleep.specify" :disabled="showOnly" type="text" class="form-input"
                style="flex: 1" :placeholder="$t('studentDailyActivity.pleaseSpecify')" />
            </div>
          </div>
        </div>

        <!-- For afternoon snack I had -->
        <div class="row">
          <label class="ts-whitespace-no-wrap col-form-label col-4">
            {{ $t('studentDailyActivity.forAfternoonSnackIHad') }}
          </label>
          <div class="col-8">
            <div class="d-flex flex-nowrap align-items-center">
              <div class="col-form-label ts-whitespace-no-wrap mr-3">
                <a-checkbox v-model="model.meta.afternoon_snack.fruits" :disabled="showOnly">
                  {{ $t('studentDailyActivity.fruits') }}
                </a-checkbox>
              </div>
              <div class="col-form-label ts-whitespace-no-wrap mr-3">
                <a-checkbox v-model="model.meta.afternoon_snack.bread" :disabled="showOnly">
                  {{ $t('studentDailyActivity.bread') }}
                </a-checkbox>
              </div>
              <div class="col-form-label ts-whitespace-no-wrap mr-3">
                <a-checkbox v-model="model.meta.afternoon_snack.others" :disabled="showOnly">
                  {{ $t('studentDailyActivity.others') }}
                </a-checkbox>
              </div>
              <input v-model="model.meta.afternoon_snack.specify"
                :disabled="showOnly || !model.meta.afternoon_snack.others" type="text" class="form-input"
                style="flex: 1" :placeholder="$t('studentDailyActivity.pleaseSpecify')" />
            </div>
          </div>
        </div>

        <!-- Today I drank water -->
        <div class="row">
          <label class="ts-whitespace-no-wrap col-form-label col-4">
            {{ $t('studentDailyActivity.todayIDrankWater') }}
          </label>
          <div class="col-8">
            <div class="row">
              <div class="col-2 col-form-label" v-for="n in 5" :key="'water-' + n">
                <a-checkbox :checked="model.meta.drinks.water === n" @change="onSelectWaterBottle(n)"
                  :disabled="showOnly">
                  {{ n }} {{ n === 1 ? $t('studentDailyActivity.bottle') : $t('studentDailyActivity.bottles') }}
                </a-checkbox>
              </div>
            </div>
          </div>
        </div>

        <!-- Today I drank milk -->
        <div class="row">
          <label class="ts-whitespace-no-wrap col-form-label col-4">
            {{ $t('studentDailyActivity.todayIDrankMilk') }}
          </label>
          <div class="col-8">
            <div class="row">
              <div class="col-2 col-form-label" v-for="n in 5" :key="'milk-' + n">
                <a-checkbox :checked="model.meta.drinks.milk === n" @change="onSelectMilkBottle(n)"
                  :disabled="showOnly">
                  {{ n }} {{ n === 1 ? $t('studentDailyActivity.bottle') : $t('studentDailyActivity.bottles') }}
                </a-checkbox>
              </div>
            </div>
          </div>
        </div>

        <!-- My mood today was -->
        <div class="row">
          <label class="ts-whitespace-no-wrap col-form-label col-4">
            {{ $t('studentDailyActivity.myMoodTodayWas') }}
          </label>
          <div class="col-8">
            <div class="d-flex flex-nowrap align-items-center">
              <div class="col-form-label ts-whitespace-no-wrap mr-3" v-for="mood in moods" :key="mood.value">
                <a-checkbox :checked="model.meta.mood === mood.value" @change="onSelectMood(mood.value)"
                  :disabled="showOnly">
                  {{ mood.emoji }} {{ mood.label }}
                </a-checkbox>
              </div>
            </div>
          </div>
        </div>

        <!-- Please bring following supplies for me -->
        <div class="row">
          <label class="ts-whitespace-no-wrap col-form-label col-12">
            {{ $t('studentDailyActivity.PleaseBringFollowingSuppliesForMe') }}
          </label>
        </div>
        <div class="row">
          <div class="col-12">
            <div class="d-flex flex-nowrap align-items-center">
              <div class="col-form-label ts-whitespace-no-wrap mr-3">
                <a-checkbox v-model="model.meta.supplies.change_of_clothes" :disabled="showOnly">
                  {{ $t('studentDailyActivity.changeOfClothes') }}
                </a-checkbox>
              </div>
              <div class="col-form-label ts-whitespace-no-wrap mr-3">
                <a-checkbox v-model="model.meta.supplies.change_nappies" :disabled="showOnly">
                  {{ $t('studentDailyActivity.changeNappies') }}
                </a-checkbox>
              </div>
              <div class="col-form-label ts-whitespace-no-wrap mr-3">
                <a-checkbox v-model="model.meta.supplies.water_bottle" :disabled="showOnly">
                  {{ $t('studentDailyActivity.waterBottle') }}
                </a-checkbox>
              </div>
              <div class="col-form-label ts-whitespace-no-wrap mr-3">
                <a-checkbox v-model="model.meta.supplies.others" :disabled="showOnly">
                  {{ $t('studentDailyActivity.others') }}
                </a-checkbox>
              </div>
              <input v-model="model.meta.supplies.specify"
                :disabled="showOnly || !model.meta.supplies.others" type="text" class="form-input"
                style="flex: 1" :placeholder="$t('studentDailyActivity.pleaseSpecify')" />
            </div>
          </div>
        </div>

        <!-- Temperature check -->
        <div class="row">
          <label class="ts-whitespace-no-wrap col-form-label col-4">
            {{ $t('studentDailyActivity.temperatureCheck') }}
          </label>
          <div class="col-8">
            <div class="d-flex flex-nowrap align-items-center">
              <div class="input-group mr-3" style="width: 150px; flex-shrink: 0">
                <input v-model="model.meta.temperature.value" :disabled="showOnly" type="text"
                  class="form-input" />
                <span class="input-group-text">&deg;C</span>
              </div>
              <input v-model="model.meta.temperature.specify" :disabled="showOnly" type="text"
                class="form-input" style="flex: 1" :placeholder="$t('studentDailyActivity.pleaseSpecify')" />
            </div>
          </div>
        </div>

        <!-- Message from teacher -->
        <div class="row">
          <div class="col-12">
            <div class="row">
              <label class="ts-whitespace-no-wrap col-form-label col-12">
                {{ $t('studentDailyActivity.messageFromTeacher') }}
              </label>
            </div>
            <div class="row">
              <div class="col-12">
                <textarea v-model="model.message_from_teacher" :disabled="showOnly" rows="3"
                  class="form-control"></textarea>
              </div>
            </div>
          </div>
        </div>

        <!-- Message from parents -->
        <div class="row">
          <div class="col-12">
            <div class="row">
              <label class="ts-whitespace-no-wrap col-form-label col-12">
                {{ $t('studentDailyActivity.messageFromParents') }}
              </label>
            </div>
            <div class="row">
              <div class="col-12">
                <textarea v-model="model.message_from_parent" :disabled="showOnly" rows="3"
                  class="form-control"></textarea>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="row ts-mt-2" v-if="!showOnly">
        <div class="col-md-12 ts-flex ts-justify-end ts-space-x-3">
          <ts-button class="mr-2" @click.prevent="() => $emit('cancel')">
            {{ $t('cancel') }}
          </ts-button>
          <ts-button v-if="!isUpdate" color="primary" outline :waiting="waiting_new" :disabled="waiting_new || waiting"
            @click.prevent="onSaveAddNew">
            {{ $t('save_add_new') }}
          </ts-button>
          <ts-button v-if="!isUpdate" color="primary" @click.prevent="onSave">
            {{ $t('save') }}
          </ts-button>
          <ts-button v-if="isUpdate" color="primary" @click.prevent="onUpdate">
            {{ $t('update') }}
          </ts-button>
        </div>
      </div>
    </div>
  </ts-loading-banner>
</template>

<script>
import { isEmpty, debounce, merge } from 'lodash'
import { Errors } from 'form-backend-validation'
import { mapActions, mapState } from 'vuex'
import moment from 'moment'

const defaultMeta = () => ({
  special_activity: '',
  morning_snack: { fruits: false, bread: false, others: false, specify: '' },
  lunch: { dishes: null, others: false, specify: '' },
  sleep: { yes: false, no: false, specify: '' },
  afternoon_snack: { fruits: false, bread: false, others: false, specify: '' },
  drinks: { water: null, milk: null },
  mood: null,
  temperature: { value: '', specify: '' },
  supplies: { change_of_clothes: false, change_nappies: false, water_bottle: false, others: false, specify: '' }
})

export default {
  name: 'studentDailyActivityForm',
  props: ['showOnlyData'],
  data() {
    return {
      waiting: false,
      waiting_new: false,
      showOnly: false,
      fetching_student: false,
      errors: new Errors(),
      classlists: [],
      studentlists: [],
      moods: [
        { value: 'joyful', emoji: '😄', label: 'Joyful' },
        { value: 'so-so', emoji: '😐', label: 'So-so' },
        { value: 'sad', emoji: '😢', label: 'Sad' }
      ],
      model: {
        id: null,
        student_id: null,
        class_id: null,
        activity_date: moment().format('DD-MM-YYYY'),
        meta: defaultMeta(),
        message_from_teacher: null,
        message_from_parent: null
      }
    }
  },
  async created() {
    this.showOnly = this.showOnlyData
    await this.setEditData()
    await this.fetchClass()
  },
  computed: {
    ...mapState('studentDailyActivity', ['edit_data']),
    isUpdate() {
      return !isEmpty(this.edit_data)
    },
    isStaff() {
      if (this.AllowAllAboutMyDay) {
        return false
      } else {
        return this.$store.getters.isStaffLogin
      }
    },
    isTeacher() {
      if (this.AllowAllAboutMyDay) {
        return false
      } else {
        return this.$store.getters.isTeacherLogin
      }
    },
    AllowAllAboutMyDay() {
      return this.$store.state.authUser.is_allow_full_all_about_my_day
        ? this.$store.state.authUser.is_allow_full_all_about_my_day
        : false
    }
  },
  methods: {
    ...mapActions('studentDailyActivity', ['getClass', 'getStudent', 'getFindData']),

    onSleepYes() {
      this.model.meta.sleep.no = false
    },
    onSleepNo() {
      this.model.meta.sleep.yes = false
    },
    onSelectLunchDish(n) {
      this.model.meta.lunch.dishes = this.model.meta.lunch.dishes === n ? null : n
    },
    onSelectWaterBottle(n) {
      this.model.meta.drinks.water = this.model.meta.drinks.water === n ? null : n
    },
    onSelectMilkBottle(n) {
      this.model.meta.drinks.milk = this.model.meta.drinks.milk === n ? null : n
    },
    onSelectMood(value) {
      if (this.showOnly) return
      this.model.meta.mood = this.model.meta.mood === value ? null : value
    },
    fetchClass(params) {
      this.waiting = true
      this.getClass(params)
        .then(response => {
          this.classlists = response.data.map(el => ({
            value: el.class_id,
            label: el.class_name
          }))
        })
        .finally(() => {
          this.waiting = false
        })
    },
    fetchStudentList() {
      this.waiting = true
      this.getStudent({ class_id: this.model.class_id })
        .then(response => {
          this.studentlists = response.data.map(el => ({
            value: el.student_id,
            label: el.card_number + '|' + el.full_name_en + '|' + el.gender
          }))
        })
        .finally(() => {
          this.waiting = false
        })
    },
    onChangeActivityDate(dateString) {
      this.model.activity_date = dateString
      this.checkData()
    },
    checkData() {
      this.waiting = true
      this.getFindData({
        class_id: this.model.class_id,
        student_id: this.model.student_id,
        activity_date: this.model.activity_date
      })
        .then(async response => {
          if (response.data.length > 0) {
            await this.setFindData(response.data[0])
          } else {
            this.clearFindData()
          }
        })
        .finally(() => {
          this.waiting = false
        })
    },
    onSave() {
      this.errors = new Errors()
      this.waiting = true
      this.$store
        .dispatch('studentDailyActivity/store', Object.assign({}, this.model))
        .then(response => {
          this.$toasted.success(response.message)
          this.$emit('fetchData')
          this.$emit('cancel')
        })
        .catch(error => {
          this.errors = new Errors(error.errors)
          this.$toasted.error(error.message)
        })
        .finally(() => {
          this.waiting = false
        })
    },
    onSaveAddNew() {
      this.errors = new Errors()
      this.waiting = true
      this.$store
        .dispatch('studentDailyActivity/store', Object.assign({}, this.model))
        .then(response => {
          this.$toasted.success(response.message)
          this.clearEdit()
        })
        .catch(error => {
          this.errors = new Errors(error.errors)
          this.$toasted.error(error.message)
        })
        .finally(() => {
          this.waiting = false
        })
    },
    onUpdate() {
      this.waiting = true
      this.$store
        .dispatch('studentDailyActivity/update', this.model)
        .then(response => {
          this.$toasted.success(response.message)
          this.$emit('fetchData')
          this.$emit('cancel')
        })
        .catch(error => {
          this.errors = new Errors(error.errors)
          this.$toasted.error(error.message)
        })
        .finally(() => {
          this.waiting = false
        })
    },
    parseMeta(meta) {
      let parsed = meta || {}
      if (typeof parsed === 'string') {
        try { parsed = JSON.parse(parsed) } catch (e) { parsed = {} }
      }
      return merge(defaultMeta(), parsed)
    },
    setEditData() {
      if (!isEmpty(this.edit_data)) {
        this.model.id = this.edit_data.id
        this.model.student_id = this.edit_data.student_id
        this.model.class_id = this.edit_data.class_id
        this.model.activity_date = this.edit_data.activity_date
        this.model.meta = this.parseMeta(this.edit_data.meta)
        this.model.message_from_teacher = this.edit_data.message_from_teacher
        this.model.message_from_parent = this.edit_data.message_from_parent
      }
    },
    setFindData(data) {
      if (!isEmpty(data)) {
        this.model.id = data.id
        this.model.student_id = data.student_id
        this.model.class_id = data.class_id
        this.model.activity_date = data.activity_date
        this.model.meta = this.parseMeta(data.meta)
        this.model.message_from_teacher = data.message_from_teacher
        this.model.message_from_parent = data.message_from_parent
      }
    },
    clearEdit() {
      this.model.id = null
      this.model.student_id = null
      this.model.class_id = null
      this.model.activity_date = moment().format('DD-MM-YYYY')
      this.model.meta = defaultMeta()
      this.model.message_from_teacher = null
      this.model.message_from_parent = null
      this.showOnly = false
    },
    clearFindData() {
      this.model.id = null
      this.model.meta = defaultMeta()
      this.model.message_from_teacher = null
      this.model.message_from_parent = null
      this.showOnly = false
    }
  },
  watch: {
    'model.class_id': debounce(function () {
      this.fetchStudentList()
    }, 500)
  }
}
</script>

<style lang="scss" scoped>
.form-input {
  line-height: 1.42857143;
  padding: 6px 12px;
  width: 100%;
  border-bottom: dotted 1px;
}

.form-input:focus {
  outline: none;
}

.input-group {
  display: flex;
  align-items: center;
}

.input-group .form-input {
  flex: 1;
}

.input-group-text {
  padding: 6px 8px;
  font-weight: 600;
}
</style>
